import React from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * StatTile — mobile-native tile primitive for dashboard stat grids and
 * quick-action grids. Replaces the inline pattern that rendered tiles as
 * plain white blocks with a number + label and barely-visible borders.
 *
 * Visual upgrade (Feb 2026):
 *   - Tinted icon chip on the left so each tile has a focal point.
 *   - Soft 2-layer card shadow (`shadow-tile`) so the tile lifts off the
 *     warm `#F7F7F5` page background.
 *   - Chevron right hint so the tap affordance reads as native iOS.
 *   - Press feedback via `active:scale-[0.99]` for tactile feel.
 *
 * No business-logic change: this is a `<button type="button">` wrapper.
 * The caller still passes onClick, label, value, tone, icon, and testid
 * — all of which preserve existing dashboard wiring.
 *
 * Props:
 *   icon       — lucide-react icon component (required)
 *   tone       — visual tint of the icon chip: 'emerald' | 'amber' |
 *                'rose' | 'blue' | 'purple' | 'gold' | 'neutral'
 *   value      — large numeric/text shown above the label (optional)
 *   label      — short descriptive label below the value (required)
 *   sublabel   — even smaller line under the label (optional, used for
 *                Payments tile's "Action needed" red sub-line)
 *   sublabelTone — color for the sublabel (e.g. 'rose')
 *   active     — when value > 0, brighten the value color via tone
 *   onClick    — tap handler
 *   testid     — preserved data-testid for testing agent
 */
const TONE_MAP = {
  emerald: { chip: 'bg-emerald-50 text-emerald-600', value: 'text-emerald-600' },
  amber:   { chip: 'bg-amber-50 text-amber-600',     value: 'text-amber-600' },
  rose:    { chip: 'bg-rose-50 text-rose-600',       value: 'text-rose-600' },
  blue:    { chip: 'bg-blue-50 text-blue-600',       value: 'text-blue-600' },
  purple:  { chip: 'bg-purple-50 text-purple-600',   value: 'text-purple-600' },
  gold:    { chip: 'bg-amber-50 text-amber-700',     value: 'text-amber-700' },
  neutral: { chip: 'bg-gray-100 text-gray-500',      value: 'text-gray-900' },
};

const SUBLABEL_TONE_MAP = {
  rose: 'text-rose-600',
  amber: 'text-amber-600',
  emerald: 'text-emerald-600',
  gray: 'text-gray-500',
};

export const StatTile = ({
  icon: Icon,
  tone = 'neutral',
  value,
  label,
  sublabel,
  sublabelTone = 'gray',
  active = false,
  onClick,
  testid,
  className,
  ...rest
}) => {
  const t = TONE_MAP[tone] || TONE_MAP.neutral;
  const valueColor = active ? t.value : 'text-gray-900';
  const subColor = SUBLABEL_TONE_MAP[sublabelTone] || SUBLABEL_TONE_MAP.gray;

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={cn(
        'group w-full flex items-center gap-3 rounded-2xl bg-white border border-gray-100/80',
        'px-3.5 py-3 text-left',
        'shadow-tile transition-all duration-150',
        'hover:border-gray-200 hover:shadow-card',
        'active:scale-[0.99] active:shadow-tile',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/40',
        className
      )}
      {...rest}
    >
      {Icon && (
        <span
          className={cn(
            'h-10 w-10 rounded-xl flex items-center justify-center shrink-0',
            t.chip
          )}
          aria-hidden="true"
        >
          <Icon className="h-[18px] w-[18px]" />
        </span>
      )}
      <div className="flex-1 min-w-0">
        {value !== undefined && value !== null && (
          <p className={cn('text-[20px] font-semibold leading-none', valueColor)}>
            {value}
          </p>
        )}
        <p
          className={cn(
            'text-[12px] font-medium text-gray-600',
            // Tiles WITH a value (e.g. "Your Business" stats) stay locked
            // to a single truncated line so all four tile heights match.
            // Tiles WITHOUT a value (e.g. "Quick Actions") allow the label
            // to wrap up to 2 lines so 13-15 char labels like
            // "Manage Services" / "Manage Schedule" / "View Requests"
            // render fully instead of being cut to "Manage S…".
            value !== undefined && value !== null
              ? 'mt-1 truncate'
              : 'leading-tight line-clamp-2'
          )}
        >
          {label}
        </p>
        {sublabel && (
          <p className={cn('text-[11px] font-medium mt-0.5', subColor)}>
            {sublabel}
          </p>
        )}
      </div>
      <ChevronRight
        className={cn(
          'h-4 w-4 text-gray-300 shrink-0 transition-colors group-hover:text-gray-400',
          // Hide the chevron on tiles that show a value (e.g. "Your
          // Business" stat grid). The large numeric value + tinted icon
          // chip already signal tappability, and removing the chevron
          // gives ~20px back to the label so single-line tiles like
          // "Needs Response" / "In Progress" fit without ellipsis while
          // still keeping all four tile heights aligned.
          value !== undefined && value !== null ? 'hidden' : ''
        )}
      />
    </button>
  );
};

export default StatTile;
